/**
 * EOC (End of Call) Report Form - Public shareable page for closers
 *
 * Accessible at /eoc (no auth required).
 * Submits to /api/eoc/submit which:
 *   1. Looks up UTM data from opt-ins/applications by prospect email
 *   2. Writes to MongoDB (primary store / fallback)
 *   3. Appends to the Data Hub EOC sheet tab (best-effort)
 */

import { useState, useEffect } from "react";
import { EOCFormLayout, FormField, TextInput, TextArea, Select } from "./EOCFormLayout";

export interface FormFieldConfig {
  key: string;
  label: string;
  type: "text" | "number" | "date" | "textarea" | "select";
  required?: boolean;
  enabled: boolean;
  options?: string[];
}

interface EOCConfigResponse {
  closers?: string[];
  setters?: string[];
  formFields?: FormFieldConfig[];
}

interface FormState {
  dateCallWasTaken: string;
  prospectName: string;
  prospectEmail: string;
  prospectNumber: string;
  closerName: string;
  setterName: string;
  offerPitched: string;
  callOutcome: string;
  cashCollected: string;
  revenueGenerated: string;
  paymentMethod: string;
  paymentPlan: string;
  customPaymentPlanDetails: string;
  callNotes: string;
  leadSource: string;
}

const EMPTY: FormState = {
  dateCallWasTaken: new Date().toISOString().split("T")[0],
  prospectName: "",
  prospectEmail: "",
  prospectNumber: "",
  closerName: "",
  setterName: "",
  offerPitched: "",
  callOutcome: "",
  cashCollected: "",
  revenueGenerated: "",
  paymentMethod: "",
  paymentPlan: "",
  customPaymentPlanDetails: "",
  callNotes: "",
  leadSource: "",
};

function toOptions(opts: string[]): { value: string; label: string }[] {
  return opts.map(o => ({ value: o, label: o }));
}

export function EOCForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sheetWarning, setSheetWarning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closers, setClosers] = useState<string[]>([]);
  const [setters, setSetters] = useState<string[]>([]);
  const [formFields, setFormFields] = useState<FormFieldConfig[]>([]);

  useEffect(() => {
    fetch("/api/eoc/config")
      .then(r => r.json())
      .then((d: EOCConfigResponse) => {
        setClosers(d.closers || []);
        setSetters(d.setters || []);
        setFormFields(d.formFields || []);
      })
      .catch(() => {/* non-fatal */});
  }, []);

  const set = (field: keyof FormState) => (value: string) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch("/api/eoc/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          cashCollected: form.cashCollected ? parseFloat(form.cashCollected) : 0,
          revenueGenerated: form.revenueGenerated ? parseFloat(form.revenueGenerated) : 0,
        }),
      });
      const data = await res.json() as { success: boolean; sheetSynced?: boolean; error?: string };
      if (!data.success) throw new Error(data.error || "Submission failed");
      setSheetWarning(data.sheetSynced === false);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <EOCFormLayout title="EOC Report" subtitle="End of Call Report">
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">EOC Submitted!</h2>
          {sheetWarning ? (
            <p className="text-amber-400/80 text-sm mb-2">
              Saved to system. Google Sheets sync will catch up on next sync — your data is safe.
            </p>
          ) : (
            <p className="text-white/60 mb-8">Your end of call report has been recorded.</p>
          )}
          <button onClick={() => { setSubmitted(false); setSheetWarning(false); setForm(EMPTY); }}
            className="px-6 py-3 rounded-xl bg-[#1f1f2e] text-white font-medium hover:bg-[#2a2a3e] transition-all mt-6">
            Submit Another Report
          </button>
        </div>
      </EOCFormLayout>
    );
  }

  // Config helpers — fall back to defaults if field not in config yet
  const getFieldCfg = (key: string) => formFields.find(f => f.key === key);
  const lbl = (key: string, fallback: string) => getFieldCfg(key)?.label ?? fallback;
  const isRequired = (key: string, fallback = false) => getFieldCfg(key)?.required ?? fallback;
  const isEnabled = (key: string) => getFieldCfg(key)?.enabled ?? true;
  const getOpts = (key: string, fallback: string[]) => {
    const f = getFieldCfg(key);
    return f?.options?.length ? f.options : fallback;
  };
  const getType = (key: string, fallback: FormFieldConfig["type"]) =>
    (getFieldCfg(key)?.type as FormFieldConfig["type"]) ?? fallback;

  const renderField = (key: keyof FormState, fallbackOpts: string[], placeholder: string, req?: boolean) => {
    const type = getType(key, "select");
    if (type !== "select") {
      return <TextInput value={form[key] as string} onChange={set(key)} required={req} />;
    }
    return (
      <Select
        value={form[key] as string}
        onChange={set(key)}
        options={toOptions(getOpts(key, fallbackOpts))}
        placeholder={placeholder}
        required={req}
      />
    );
  };

  const closerOptions = closers.map(c => ({ value: c, label: c }));
  const setterOptions = setters.map(s => ({ value: s, label: s }));

  return (
    <EOCFormLayout title="EOC Report" subtitle="Fill this out after every call">
      {error && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 mb-6">
          <p className="text-red-400 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 text-sm underline mt-1 hover:text-red-300">Dismiss</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* DATE */}
        {isEnabled("dateCallWasTaken") && (
          <FormField label={lbl("dateCallWasTaken", "Date Call Was Taken")} required={isRequired("dateCallWasTaken", true)}>
            <TextInput type="date" value={form.dateCallWasTaken} onChange={set("dateCallWasTaken")} required={isRequired("dateCallWasTaken", true)} />
          </FormField>
        )}

        {/* PROSPECT INFO */}
        {(isEnabled("prospectName") || isEnabled("prospectNumber")) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isEnabled("prospectName") && (
              <FormField label={lbl("prospectName", "Prospect Full Name")} required={isRequired("prospectName", true)}>
                <TextInput value={form.prospectName} onChange={set("prospectName")} placeholder="John Smith" required={isRequired("prospectName", true)} />
              </FormField>
            )}
            {isEnabled("prospectNumber") && (
              <FormField label={lbl("prospectNumber", "Prospect Phone")}>
                <TextInput value={form.prospectNumber} onChange={set("prospectNumber")} placeholder="+1 555 000 0000" />
              </FormField>
            )}
          </div>
        )}

        {isEnabled("prospectEmail") && (
          <FormField label={lbl("prospectEmail", "Prospect Email")} required={isRequired("prospectEmail", true)} hint="UTM data will be auto-populated from this email">
            <TextInput value={form.prospectEmail} onChange={set("prospectEmail")} placeholder="prospect@email.com" required={isRequired("prospectEmail", true)} />
          </FormField>
        )}

        {/* TEAM */}
        {(isEnabled("closerName") || isEnabled("setterName")) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isEnabled("closerName") && (
              <FormField label={lbl("closerName", "Closer Name")} required={isRequired("closerName", true)}>
                {closerOptions.length > 0 ? (
                  <Select value={form.closerName} onChange={set("closerName")} options={closerOptions} placeholder="Select closer..." required={isRequired("closerName", true)} />
                ) : (
                  <TextInput value={form.closerName} onChange={set("closerName")} placeholder="Your name" required={isRequired("closerName", true)} />
                )}
              </FormField>
            )}
            {isEnabled("setterName") && (
              <FormField label={lbl("setterName", "Setter Name")}>
                {setterOptions.length > 0 ? (
                  <Select value={form.setterName} onChange={set("setterName")} options={setterOptions} placeholder="Select setter..." />
                ) : (
                  <TextInput value={form.setterName} onChange={set("setterName")} placeholder="Setter who booked the call" />
                )}
              </FormField>
            )}
          </div>
        )}

        {/* CALL DETAILS */}
        {isEnabled("offerPitched") && (
          <FormField label={lbl("offerPitched", "Offer Pitched?")}>
            {renderField("offerPitched", ["Yes", "No"], "Select...")}
          </FormField>
        )}

        {isEnabled("callOutcome") && (
          <FormField label={lbl("callOutcome", "Call Outcome")} required={isRequired("callOutcome", true)}>
            {renderField("callOutcome", ["Closed", "Deposit Collected", "Upsell", "Downsell", "Taken", "Not Closed", "Follow-up Scheduled", "Rescheduled", "No-show", "Cancelled"], "Select outcome...", isRequired("callOutcome", true))}
          </FormField>
        )}

        {/* FINANCIALS */}
        {(isEnabled("cashCollected") || isEnabled("revenueGenerated")) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isEnabled("cashCollected") && (
              <FormField label={lbl("cashCollected", "Cash Collected ($)")}>
                <TextInput type="number" value={form.cashCollected} onChange={set("cashCollected")} placeholder="0.00" min={0} step={0.01} />
              </FormField>
            )}
            {isEnabled("revenueGenerated") && (
              <FormField label={lbl("revenueGenerated", "Revenue Generated ($)")}>
                <TextInput type="number" value={form.revenueGenerated} onChange={set("revenueGenerated")} placeholder="0.00" min={0} step={0.01} />
              </FormField>
            )}
          </div>
        )}

        {/* PAYMENT */}
        {(isEnabled("paymentMethod") || isEnabled("paymentPlan")) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {isEnabled("paymentMethod") && (
              <FormField label={lbl("paymentMethod", "Payment Method")}>
                {renderField("paymentMethod", ["Credit Card", "Bank Transfer", "Cash", "WOP", "Fan Basis", "Other"], "Select method...")}
              </FormField>
            )}
            {isEnabled("paymentPlan") && (
              <FormField label={lbl("paymentPlan", "Payment Plan")}>
                {renderField("paymentPlan", ["Pay in Full", "$1k/month for 4 months", "$2k/month for 2 months", "Custom"], "Select plan...")}
              </FormField>
            )}
          </div>
        )}

        {isEnabled("customPaymentPlanDetails") && form.paymentPlan === "Custom" && (
          <FormField label={lbl("customPaymentPlanDetails", "Custom Payment Plan Details")}>
            <TextInput value={form.customPaymentPlanDetails} onChange={set("customPaymentPlanDetails")} placeholder="Describe the custom plan..." />
          </FormField>
        )}

        {/* LEAD SOURCE */}
        {isEnabled("leadSource") && (
          <FormField label={lbl("leadSource", "New Lead or Referral?")}>
            {renderField("leadSource", ["New Lead", "Referral"], "Select...")}
          </FormField>
        )}

        {/* NOTES */}
        {isEnabled("callNotes") && (
          <FormField label={lbl("callNotes", "Call Notes")}>
            <TextArea value={form.callNotes} onChange={set("callNotes")} placeholder="Add any notes about the call..." rows={4} />
          </FormField>
        )}

        <button type="submit" disabled={loading}
          className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white font-semibold text-base hover:shadow-lg hover:shadow-[var(--brand-primary)]/25 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2">
          {loading ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Submitting...</span>
            </>
          ) : "Submit EOC Report"}
        </button>
      </form>
    </EOCFormLayout>
  );
}
